// services/CollaboratorService.js
import { projectCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { ProjectHeadersService } from '../../../shared/middleware/services/ProjectHeadersService';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { FILE_ENDPOINTS, PROJECT_ENDPOINTS } from '../../../shared/middleware/config/projectApiEndpoints';
import logger from '../../../shared/services/logging/logger';

export const CollaboratorService = {
  /**
   * Add a collaborator (user) to a project
   */
  addCollaborator: async (projectId, collaboratorData, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      // Add user information for permission validation
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Create new collaborator object
      const newCollaborator = {
        userId: collaboratorData.userId || collaboratorData.email,
        permissions: getPermissionsArray(collaboratorData.role)
      };
      
      // Check if user already exists
      const existingUserIndex = currentPermissions.allowedUsers.findIndex(
        user => user.userId === newCollaborator.userId
      );
      
      if (existingUserIndex !== -1) {
        // Update existing user permissions
        currentPermissions.allowedUsers[existingUserIndex].permissions = newCollaborator.permissions;
      } else {
        // Add new user
        currentPermissions.allowedUsers.push(newCollaborator);
      }
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
      
      const displayName = collaboratorData.name && collaboratorData.surname 
        ? `${collaboratorData.name} ${collaboratorData.surname}`.trim()
        : collaboratorData.email;
      
      return {
        success: true,
        collaborator: newCollaborator,
        message: `${displayName} has been added as a collaborator with ${collaboratorData.role} permissions`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Add a group collaborator to a project
   */
  addGroupCollaborator: async (projectId, groupData, userData) => {
    try {
      
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      // Add user information for permission validation
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Create new group collaborator object
      const newGroupCollaborator = {
        groupId: groupData.groupId,
        permissions: getPermissionsArray(groupData.role)
      };
      
      // Check if group already exists
      const existingGroupIndex = currentPermissions.allowedGroups.findIndex(
        group => group.groupId === newGroupCollaborator.groupId
      );
      
      if (existingGroupIndex !== -1) {
        // Update existing group permissions
        currentPermissions.allowedGroups[existingGroupIndex].permissions = newGroupCollaborator.permissions;
      } else {
        // Add new group
        currentPermissions.allowedGroups.push(newGroupCollaborator);
      }
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
      
      return {
        success: true,
        groupCollaborator: newGroupCollaborator,
        message: `${groupData.name} team has been added as a collaborator with ${groupData.role} permissions`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Remove a collaborator from a project
   */
  removeCollaborator: async (projectId, collaboratorId, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Remove the collaborator (check by userId)
      const originalLength = currentPermissions.allowedUsers?.length || 0;
      currentPermissions.allowedUsers = (currentPermissions.allowedUsers || []).filter(
        user => user.userId !== collaboratorId
      );
      
      if (currentPermissions.allowedUsers.length === originalLength) {
        throw new Error('Collaborator not found in this project');
      }
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
    
      return {
        success: true,
        message: `${collaboratorId} has been removed from the project`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Remove a group collaborator from a project
   */
  removeGroupCollaborator: async (projectId, groupId, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Remove the group collaborator
      const originalLength = currentPermissions.allowedGroups?.length || 0;
      currentPermissions.allowedGroups = (currentPermissions.allowedGroups || []).filter(
        group => group.groupId !== groupId
      );
      
      if (currentPermissions.allowedGroups.length === originalLength) {
        throw new Error('Group collaborator not found in this project');
      }
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
      return {
        success: true,
        message: `Group has been removed from the project`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Get all collaborators for a project
   */
  getCollaborators: async (projectId, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      try {
        const permissions = await getExistingPermissions(projectId, headers, userData);
        
        return {
          owner: permissions.owner || null,
          collaborators: permissions.allowedUsers || [],
          groups: permissions.allowedGroups || []
        };
      } catch (error) {
        return {
          owner: null,
          collaborators: [],
          groups: []
        };
      }
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Update collaborator permissions
   */
  updateCollaboratorPermissions: async (projectId, collaboratorId, newRole, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Find and update the collaborator
      const collaboratorIndex = (currentPermissions.allowedUsers || []).findIndex(
        user => user.userId === collaboratorId
      );
      
      if (collaboratorIndex === -1) {
        throw new Error('Collaborator not found');
      }
      
      currentPermissions.allowedUsers[collaboratorIndex].permissions = getPermissionsArray(newRole);
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
      
      return {
        success: true,
        message: `${collaboratorId} permissions updated to ${newRole}`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Toggle favorite status for a user on a project
   */
  toggleUserFavorite: async (projectId, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      const currentUserId = userData.userId || userData.id;

      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);

      // Find the user in allowedUsers
      const userIndex = (currentPermissions.allowedUsers || []).findIndex(
        user => user.userId === currentUserId
      );

      let newFavoriteStatus = false;

      if (userIndex !== -1) {
        // User exists - toggle their favorite status
        const currentFavorite = currentPermissions.allowedUsers[userIndex].favourite || false;
        newFavoriteStatus = !currentFavorite;
        currentPermissions.allowedUsers[userIndex].favourite = newFavoriteStatus;
      } else {
        // User not in allowedUsers - this shouldn't typically happen
        // but we can add them with just favorite status
        logger.warn('User not found in allowedUsers, adding with favorite status', {
          projectId,
          userId: currentUserId
        });
        newFavoriteStatus = true;
        currentPermissions.allowedUsers.push({
          userId: currentUserId,
          permissions: ['read'], // Default minimal permissions
          favourite: newFavoriteStatus
        });
      }

      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);

      return {
        success: true,
        favourite: newFavoriteStatus,
        message: newFavoriteStatus ? 'Project added to favorites' : 'Project removed from favorites'
      };

    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  /**
   * Update group collaborator permissions
   */
  updateGroupCollaboratorPermissions: async (projectId, groupId, newRole, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      // Get existing permissions
      const currentPermissions = await getExistingPermissions(projectId, headers, userData);
      
      // Find and update the group collaborator
      const groupIndex = (currentPermissions.allowedGroups || []).findIndex(
        group => group.groupId === groupId
      );
      
      if (groupIndex === -1) {
        throw new Error('Group collaborator not found');
      }
      
      currentPermissions.allowedGroups[groupIndex].permissions = getPermissionsArray(newRole);
      
      // Save updated permissions
      await savePermissions(projectId, currentPermissions, headers, userData);
      
      return {
        success: true,
        message: `Group permissions updated to ${newRole}`
      };
      
    } catch (error) {
      throw handleCollaboratorError(error);
    }
  },

  deleteProject: async (projectId, userData) => {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!userData) {
        throw new Error('User authentication required');
      }

      // Get base headers with authentication
      const baseHeaders = await getAuthHeaders();
      
      // Add project-specific headers (tenant context, etc.)
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      // Add user information for permission validation
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      // Add tenant ID for proper tenant isolation
      if (userData.tenantId) {
        headers['X-Tenant-ID'] = userData.tenantId;
      }
      // Make DELETE request to backend using noCacheAxios
      // Use noCacheAxios for DELETE operations as they modify data
      const response = await noCacheAxios.delete(
        PROJECT_ENDPOINTS.deleteProject(projectId),
        {
          headers,
          timeout: 30000 // 30 second timeout for delete operations
        }
      );

      // Bust the project list cache so the next getAllProjects call fetches fresh data
      invalidateCache(projectCachedAxios);

      return {
        success: true,
        message: `Project ${projectId} has been deleted successfully`,
        data: response.data
      };

    } catch (error) {

      // Handle different types of errors
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.message || error.response.data || 'Unknown error';

        switch (status) {
          case 400:
            throw new Error(`Invalid request: ${errorMessage}`);
          case 401:
            throw new Error('Authentication required. Please log in again.');
          case 403:
            throw new Error('You do not have permission to delete this project. Only project owners can delete projects.');
          case 404:
            throw new Error('Project not found or you do not have access to it.');
          case 409:
            throw new Error(`Project deletion failed: ${errorMessage}`);
          case 500:
            throw new Error('Server error occurred while deleting the project. Please try again.');
          default:
            throw new Error(`Failed to delete project: ${errorMessage} (Status: ${status})`);
        }
      } else if (error.request) {
        // Network error
        throw new Error('Network error: Unable to connect to the server. Please check your connection.');
      } else {
        // Something else went wrong
        throw new Error(error.message || 'An unexpected error occurred while deleting the project.');
      }
    }
  },

  /**
   * Delete all projects for the current tenant (admin function)
   */
  deleteAllProjects: async (userData) => {
    try {
      if (!userData) {
        throw new Error('User authentication required');
      }

      // Get base headers with authentication
      const baseHeaders = await getAuthHeaders();
      
      // Add project-specific headers (tenant context, etc.)
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      // Add user information for permission validation
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      // Add tenant ID for proper tenant isolation
      if (userData.tenantId) {
        headers['X-Tenant-ID'] = userData.tenantId;
      }

      // Make DELETE request to delete all projects endpoint using noCacheAxios
      // Use noCacheAxios for DELETE operations as they modify data
      const response = await noCacheAxios.delete(
        `${PROJECT_ENDPOINTS.getAll}/all`, // Assuming this endpoint exists
        {
          headers,
          timeout: 60000 // 60 second timeout for bulk operations
        }
      );
      return {
        success: true,
        message: 'All projects have been deleted successfully',
        data: response.data
      };

    } catch (error) {

      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.message || error.response.data || 'Unknown error';

        switch (status) {
          case 401:
            throw new Error('Authentication required. Please log in again.');
          case 403:
            throw new Error('You do not have permission to delete all projects. Administrator access required.');
          case 500:
            throw new Error('Server error occurred while deleting projects. Please try again.');
          default:
            throw new Error(`Failed to delete all projects: ${errorMessage} (Status: ${status})`);
        }
      } else if (error.request) {
        throw new Error('Network error: Unable to connect to the server. Please check your connection.');
      } else {
        throw new Error(error.message || 'An unexpected error occurred while deleting all projects.');
      }
    }
  },
};

// Helper Functions

/**
 * Fetch with automatic retry on 429 Too Many Requests.
 * Waits `delay * attempt` ms before each retry (linear backoff).
 */
const fetchWithRetry = async (fn, retries = 3, delay = 600) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const is429 = err?.response?.status === 429;
      if (is429 && attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      } else {
        throw err;
      }
    }
  }
};

/**
 * Get existing permissions from the project
 */
const getExistingPermissions = async (projectId, headers, userData) => {

  // Get project files to find permission.json location.
  // Use noCacheAxios — permission data must always be fresh to reflect
  // collaborator additions/removals. Cached responses cause flickering
  // where removed users briefly see projects they no longer have access to.
  let permissionFilePath = '';
  const projectFiles = await fetchWithRetry(() =>
    noCacheAxios.get(FILE_ENDPOINTS.getFiles(projectId, ''), { headers })
  );
  
  let permissionFile = projectFiles.data?.find(file => 
    file.name === 'permission.json' && !file.directory
  );
  
  if (permissionFile) {
    permissionFilePath = '';
  } else {
    // Look for permission.json in project subfolders
    const projectFolders = projectFiles.data?.filter(item => item.directory);
    
    for (const folder of projectFolders || []) {
      try {
        const folderFiles = await fetchWithRetry(() =>
          noCacheAxios.get(FILE_ENDPOINTS.getFiles(projectId, folder.name), { headers })
        );
        
        const permInFolder = folderFiles.data?.find(file => 
          file.name === 'permission.json' && !file.directory
        );
        
        if (permInFolder) {
          permissionFile = permInFolder;
          permissionFilePath = folder.name.replace(/\/$/, ''); // Remove trailing slash
          break;
        }
      } catch (folderErr) {
        logger.debug('Error reading folder for permission file', {
          error: folderErr.message,
          projectId,
          folderName: folder.name
        });
      }
    }
  }
  
  if (permissionFile && permissionFile.content) {
    // Parse existing permissions
    let contentString;
    if (Array.isArray(permissionFile.content)) {
      // If it's already a byte array, decode it
      contentString = new TextDecoder().decode(new Uint8Array(permissionFile.content));
    } else if (typeof permissionFile.content === 'string') {
      // Check if it looks like base64 (starts with letters/numbers, no { or other JSON chars)
      if (permissionFile.content.match(/^[A-Za-z0-9+/]+=*$/)) {
        // It's base64, decode it first
        const decodedBytes = atob(permissionFile.content);
        contentString = decodedBytes;
      } else {
        // It's already a JSON string
        contentString = permissionFile.content;
      }
    } else {
      contentString = String(permissionFile.content);
    }
    
    const permissions = JSON.parse(contentString);
    
    // Ensure all required fields exist
    if (!permissions.allowedUsers) {
      permissions.allowedUsers = [];
    }
    if (!permissions.allowedGroups) {
      permissions.allowedGroups = [];
    }
    
    permissions.updatedAt = new Date().toISOString();
    permissions._filePath = permissionFilePath; // Store the file path for saving
    permissions._isUpdate = true; // Flag to indicate this is an update
    return permissions;
  } else {
    // Create new permission structure
    return {
      owner: userData.userId,
      allowedUsers: [],
      allowedGroups: [],
      private: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _filePath: '', // Default to root
      _isUpdate: false // Flag to indicate this is a new file
    };
  }
};

/**
 * Save permissions to the project
 */
const savePermissions = async (projectId, permissions, headers, userData) => {
  const filePath = permissions._filePath || '';

  // Clean up internal fields
  delete permissions._filePath;
  delete permissions._isUpdate;

  permissions.updatedAt = new Date().toISOString();

  // Convert permissions to JSON string
  const jsonString = JSON.stringify(permissions, null, 2);

  try {
    // Import FileService dynamically to avoid circular imports
    const { FileService } = await import('../../../shared/middleware/services/FileService');

    // Use Blob → File → uploadFile pattern to avoid multipart cache error
    const blob = new Blob([jsonString], { type: 'application/json' });
    const file = new File([blob], 'permission.json', { type: 'application/json' });

    // folderPath for uploadFile (without the filename)
    const folderPath = filePath || '';

    const result = await FileService.uploadFile(
      projectId,
      folderPath,
      file,
      userData
    );

    return result;

  } catch (error) {
    logger.error('Failed to save permissions', {
      error: error.message,
      projectId,
      filePath,
      response: error.response?.data
    });

    // Provide helpful error message
    const errorMsg = error.response?.data?.message || error.response?.data || error.message;
    throw new Error(`Failed to save permissions: ${errorMsg}`);
  }
};

/**
 * Handle common collaborator errors
 */
const handleCollaboratorError = (error) => {
  const status = error.response?.status;
  const errorData = error.response?.data;

  logger.error('Collaborator operation failed', {
    status,
    error: error.message,
    data: errorData
  });

  if (status === 400) {
    const errorMsg = errorData?.message || errorData || 'Bad request';
    return new Error(`Invalid request: ${errorMsg}. Please check the data format and try again.`);
  } else if (status === 403) {
    return new Error('You do not have permission to manage collaborators for this project');
  } else if (status === 404) {
    return new Error('Project not found or you do not have access to it');
  } else if (status === 500) {
    return new Error('Server error while managing collaborators. Please try again.');
  } else {
    return new Error(error.message || 'Failed to manage collaborators');
  }
};

/**
 * Helper function to convert role to permissions array
 */
const getPermissionsArray = (role) => {
  switch (role) {
    case 'read':
      return ['read'];
    case 'write':
      return ['read', 'write'];
    case 'execute':
      return ['read', 'write', 'execute'];
    case 'admin':
      return ['read', 'write', 'execute', 'delete'];
    default:
      return ['read'];
  }
};

/**
 * Helper function to convert permissions array to role
 */
export const getPermissionRole = (permissions) => {
  if (!permissions || permissions.length === 0) return 'read';
  
  if (permissions.includes('delete')) return 'admin';
  if (permissions.includes('execute')) return 'execute';
  if (permissions.includes('write')) return 'write';
  return 'read';
};